import Bugsnag from 'lib/bugsnag'
// We have a few variables that needs to be decalred in the parent window shared context
// that should technically be handled as private. We use underscrores to indicate that as
// its generally used and understood in that way
/* eslint-disable no-underscore-dangle */
import {
  GLOBAL_EVENTS_INIT_IFRAME,
  GLOBAL_EVENTS_SET_COOKIE,
  GLOBAL_EVENTS_CHANGE_CONTAINER_CLASS,
  GLOBAL_EVENTS_FETCH_CURRENT_USER_FAILED,
  GLOBAL_EVENTS_FETCH_CURRENT_USER_SUCCESS,
  GLOBAL_EVENTS_DOMAIN_NOT_ALLOWED,
  GLOBAL_EVENTS_BOOTSTRAP_DONE,
  GLOBAL_EVENTS_SET_END_USER_ATTRIBUTE,
  GLOBAL_EVENTS_SET_DEBUG_MODE,
  GLOBAL_EVENTS_UPDATE_SETTINGS,
  GLOBAL_EVENTS_UPDATE_TRANSLATIONS,
  GLOBAL_EVENTS_CHANGE_CONTAINER_CLASS_DONE,
  GLOBAL_EVENTS_OPEN,
  GLOBAL_EVENTS_CLOSE,
  GLOBAL_EVENTS_TOGGLE,
  GLOBAL_EVENTS_RESIZE,
  GLOBAL_EVENTS_ROUTE_CHANGE,
  GLOBAL_EVENTS_GET_ROUTE,
  GLOBAL_EVENTS_LISTENER_READY,
  GLOBAL_EVENTS_OVERRIDE_TITLE,
  GLOBAL_EVENTS_RESTORE_TITLE,
  GLOBAL_EVENTS_GET_DATA,
  GLOBAL_EVENTS_GET_DATA_SUCCESS,
  GLOBAL_EVENTS_GET_DATA_FAILED,
  GLOBAL_EVENTS_LAYOUT_CHANGE,
  GLOBAL_EVENTS_DOWNLOAD_FILE,
  GLOBAL_EVENTS_PLAY_AUDIO,
} from 'ducks/global/actionTypes/events'
import { INIT_TRACKER } from 'ducks/global/tracker/actionTypes'
import {
  UnmountedError,
  ResponseError,
  DomainVerificationError,
} from 'ducks/global/errors'

import { selectEnvironmentUrlTemplate } from 'ducks/global/selectors/app'

import { debounce, isFunction } from 'lib/functions'
import { isIos } from 'lib/browser'

import {
  GROOVE_WRAPPER_ID_BASE,
  IFRAME_ID,
  IFRAME_TITLE,
  PUBLIC_API,
} from './constants'
import Tracker from './Tracker'

const ENV_URL_TEMPLATE = selectEnvironmentUrlTemplate()
const TRACKER_DISABLED_WARNING =
  'The tracker has been disabled for this widget instance. All tracking methods are unavaliable.'

const POSSIBLE_AUDIO_INTERACTION_EVENTS = ['click', 'touchstart', 'keydown']
const VIEWPORT_CONTENT =
  'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'

const {
  shopify: { consentTrackingApiVersion: SHOPIFY_CONSENT_TRACKING_API_VERSION },
} = APP_CONFIG

export default class GrooveShim {
  static PUBLIC_API = PUBLIC_API

  constructor(embeddable = {}) {
    this.embeddable = embeddable
    this.debugMode = false
    this.domainAllowed = true // optimistically assume for now
    this.isOpen = false
    this.options = {}
    this.wrapperLayout = {
      outerWidth: 0,
      outerHeight: 0,
      position: 'right',
      zIndex: Number.MAX_SAFE_INTEGER,
      offsetHorizontal: 20,
      offsetBottom: 20,
    }
    this.deferredResolutions = {}
    this.requestIdCounter = 0
    this.layoutStrategy = null
    this.runQueuedInit()
  }

  dispatchToWidget = (action, returnPromise = false) => {
    const requestId = action.requestId || this.requestIdCounter++
    let ret = null
    if (returnPromise) {
      ret = new Promise(res => {
        this.deferredResolutions[requestId] = res
      })
    }
    const finalMessage = {
      action: { ...action, requestId },
      source: 'groove-shim',
    }
    this.iframe.contentWindow.postMessage(finalMessage, '*')
    return ret
  }

  // PUBLIC API
  /*
   * Public: Initializes the widget
   *
   *   widgetUuid - The String widget unique identifier.
   *   options:
   *      zInex - The String zIndex to assign to widget wrapper container.
   *      position - The String position of the widget on the screen: right|left
   *      offset - Pixels from corner of screen, defaults to 20
   *      targetElement - The String DOM id to append widget. Default: body
   *      previewMode - Adds preview mode header to requests
   *      preferLocal - Prefer local resources, for GDPR compliance
   */
  init = (widgetUuid, options = {}) => {
    this.widgetUuid = widgetUuid
    this.wrapperId = `${GROOVE_WRAPPER_ID_BASE}-${this.widgetUuid}`
    this.environmentUrl = ENV_URL_TEMPLATE.replace(
      '%{{WIDGET_UUID}}%',
      widgetUuid
    )
    // NOTE (jscheel): We have to pass preferLocal in this way becuase it needs
    // to catch _everything_, even before the options are dispatched to the iframe
    // also, always set a value of preferLocal
    this.iframeUrl = `${
      this.environmentUrl
    }/landing?preferLocal=${!!options.preferLocal}`

    this.options = options
    this.initializeIframe(options)

    if (this.isInShopifyOnlineStore()) {
      this.shopifyCheckTrackingConsentBeforeLoad()
      return
    }
    this.loadWidget()
  }

  open = () => {
    this.dispatchToWidget({
      type: GLOBAL_EVENTS_OPEN,
      payload: {},
    })
  }

  close = () => {
    this.dispatchToWidget({
      type: GLOBAL_EVENTS_CLOSE,
      payload: {},
    })
  }

  toggle = (open = undefined) => {
    this.dispatchToWidget({
      type: GLOBAL_EVENTS_TOGGLE,
      payload: { open: open === undefined ? undefined : !!open },
    })
  }

  setAttribute = (name, value, type = 'STRING') => {
    this.ensureAllowed()
    this.ensureMounted()
    this.dispatchToWidget({
      type: GLOBAL_EVENTS_SET_END_USER_ATTRIBUTE,
      value: { name, value, token: this.getCookie(), type },
    })
  }

  debug = () => {
    this.ensureAllowed()
    this.debugMode = !this.debugMode
    //  eslint-disable-next-line no-console
    console.info(
      `[Groove] debug mode ${this.debugMode ? 'enabled' : 'disabled'}`
    )
    this.dispatchToWidget({
      type: GLOBAL_EVENTS_SET_DEBUG_MODE,
      value: this.debugMode,
    })
  }

  updateSettings = settings => {
    if (
      settings &&
      ('offsetVertical' in settings || 'offsetHorizontal' in settings)
    ) {
      if ('offsetVertical' in settings) {
        this.options.offsetVertical = settings.offsetVertical
      }
      if ('offsetHorizontal' in settings) {
        this.options.offsetHorizontal = settings.offsetHorizontal
      }
      window.dispatchEvent(new Event('resize'))
    }
    return this.dispatchToWidget(
      {
        type: GLOBAL_EVENTS_UPDATE_SETTINGS,
        payload: {
          data: settings,
        },
      },
      true
    )
  }

  updateTranslations = translations => {
    return this.dispatchToWidget(
      {
        type: GLOBAL_EVENTS_UPDATE_TRANSLATIONS,
        payload: {
          translations,
        },
      },
      true
    )
  }

  destroy = () => {
    if (this.iframe) {
      this.iframe.parentElement.removeChild(this.iframe)
    }
    if (this.wrapper) {
      this.wrapper.parentElement.removeChild(this.wrapper)
    }
    this.unpatchViewport()
    window.removeEventListener('load', this.onWindowLoad)
    window.removeEventListener('message', this.receiveMessage)
    window.removeEventListener('resize', this.handleWindowResize)
    this.removeAudioInteractionListeners()
    if (this.tracker) {
      this.tracker.destroy()
    }
    delete this.tracker
    if (
      window.groove.widget?.shim ===
      window.groove.loadedWidgets[this.widgetUuid]
    ) {
      delete window.groove.widget
    }
    delete window.groove.loadedWidgets[this.widgetUuid]

    if (this.isInShopifyOnlineStore()) {
      this.removeShopifyEventListeners()
    }
  }

  // PRIVATE METHODS
  loadWidget = () => {
    const { readyState } = document
    if (readyState === 'complete' || readyState === 'loaded') {
      this.onWindowLoad()
    } else {
      window.addEventListener('load', this.onWindowLoad)
    }
  }

  onWindowLoad = () => {
    window.removeEventListener('load', this.onWindowLoad)
    if (this.options.disableTracker !== true) this.tracker = new Tracker(this)
    this.authorizeAudio()
    this.mountIframe(this.options)
  }

  onChangeContainerClass = classnames => {
    this.iframe.className = classnames
    this.dispatchToWidget({ type: GLOBAL_EVENTS_CHANGE_CONTAINER_CLASS_DONE })
  }

  ensureMounted = () => {
    if (!document.getElementById(IFRAME_ID)) {
      throw new UnmountedError(`init needs to be called first`)
    }
  }

  ensureAllowed = () => {
    if (!this.domainAllowed) {
      throw new DomainVerificationError(
        `${window.location.host} is not permitted to use widget ID ${this.widgetUuid}`
      )
    }
  }

  receiveMessage = event => {
    if (
      event.origin === this.environmentUrl &&
      !!event &&
      !!event.data &&
      event.data.source === 'groove-widget' &&
      !!event.data.action
    ) {
      const { action } = event.data

      switch (action.type) {
        case GLOBAL_EVENTS_LISTENER_READY:
          this.dispatchToWidget({
            type: GLOBAL_EVENTS_INIT_IFRAME,
            payload: {
              widgetUuid: this.widgetUuid,
              topHost: window.location.host,
              options: this.options,
              layoutStrategy: this.layoutStrategy,
            },
          })
          this.resizeWrapper()
          break
        case INIT_TRACKER:
          if (this.tracker) this.tracker.init(action)
          break
        case GLOBAL_EVENTS_SET_COOKIE:
          document.cookie = event.data.value
          break
        case GLOBAL_EVENTS_CHANGE_CONTAINER_CLASS:
          this.onChangeContainerClass(event.data.value)
          break
        case GLOBAL_EVENTS_FETCH_CURRENT_USER_FAILED:
          if (this.onFailedCurrentUserFetch)
            this.onFailedCurrentUserFetch(
              typeof event.data.value === 'string'
                ? new ResponseError('Internal error')
                : new ResponseError(
                    event.data.value.error_message,
                    event.data.value
                  )
            )
          break
        case GLOBAL_EVENTS_FETCH_CURRENT_USER_SUCCESS:
          if (this.onSuccessfulCurrentUserFetch)
            this.onSuccessfulCurrentUserFetch(event.data.value)
          if (this.onSuccessfulFlow) this.onSuccessfulFlow(event.data.value)
          break
        case GLOBAL_EVENTS_DOMAIN_NOT_ALLOWED:
          this.handleDomainNotAllowed()
          break
        case GLOBAL_EVENTS_BOOTSTRAP_DONE:
          this.handleBootstrapDone()
          break
        case GLOBAL_EVENTS_RESIZE: {
          const optionH = parseFloat(this.options.offsetHorizontal)
          const optionV = parseFloat(this.options.offsetVertical)
          const { wrapperLayout, open } = action.payload
          this.wrapperLayout = {
            ...wrapperLayout,
            offsetHorizontal: Number.isNaN(optionH)
              ? wrapperLayout.offsetHorizontal
              : optionH,
            offsetVertical: Number.isNaN(optionV)
              ? wrapperLayout.offsetVertical
              : optionV,
          }
          this.isOpen = open
          this.resizeWrapper()
          break
        }
        case GLOBAL_EVENTS_GET_ROUTE: {
          // do stuff
          break
        }
        case GLOBAL_EVENTS_OVERRIDE_TITLE: {
          const { title } = action.payload
          this.originalDocumentTitle = document.title
          this.overridenDocumentTitle = title
          document.title = title
          break
        }
        case GLOBAL_EVENTS_RESTORE_TITLE: {
          if (
            document.title !== this.originalDocumentTitle &&
            document.title !== this.overridenDocumentTitle
          ) {
            // NOTE (jscheel): The embedding page has changed the title so we
            // should leave it alone.
            break
          }
          document.title = this.originalDocumentTitle
          break
        }
        case GLOBAL_EVENTS_GET_DATA: {
          const { dataType, requestId, payload } = action
          this.getData(dataType, requestId, payload)
          break
        }
        case GLOBAL_EVENTS_OPEN: {
          this.isOpen = true
          this.resizeWrapper()
          break
        }
        case GLOBAL_EVENTS_CLOSE: {
          this.isOpen = false
          this.resizeWrapper()
          break
        }
        case GLOBAL_EVENTS_DOWNLOAD_FILE: {
          const {
            payload: { url, fileName },
          } = action
          this.downloadFile(url, fileName)
          break
        }
        case GLOBAL_EVENTS_PLAY_AUDIO: {
          const {
            payload: { url },
          } = action
          this.playAudio(url)
          break
        }
        default:
          // Other messages can just be ignored
          break
      }

      const { requestId } = action
      if (requestId !== undefined) {
        const dfd = this.deferredResolutions[requestId]
        if (dfd) {
          dfd(action.payload)
        }
      }
    }
  }

  handleBootstrapDone = () => {
    window.groove.loadedWidgets[this.widgetUuid] = this
    this.finalizeEmbeddable()
  }

  handleDomainNotAllowed = () => {
    this.domainAllowed = false
  }

  getCookie = () => {
    const startIndex = document.cookie.indexOf(`GROOVE_AUTH-${this.widgetUuid}`)
    if (startIndex === -1) {
      return null
    }
    const startSlice = startIndex + 22
    const endIndex = document.cookie.slice(startIndex).indexOf(';')
    if (endIndex === -1) {
      return document.cookie.slice(startSlice)
    }
    return document.cookie.slice(startSlice, startIndex + endIndex)
  }

  // Added payload for possible future use where we need to pass parameters
  // to get data requests
  // eslint-disable-next-line no-unused-vars
  getData = (dataType, requestId, payload) => {
    switch (dataType) {
      case 'url':
        this.dispatchToWidget({
          type: GLOBAL_EVENTS_GET_DATA_SUCCESS,
          requestId,
          payload: {
            data: window.location.href,
          },
        })
        break
      default:
        this.dispatchToWidget({
          type: GLOBAL_EVENTS_GET_DATA_FAILED,
          requestId,
          payload: {
            error: `Unknown data type [${dataType}]`,
          },
        })
    }
  }

  initializeIframe = () => {
    if (!document.getElementById(IFRAME_ID)) {
      const iframe = document.createElement('iframe')
      iframe.style.border = 0
      iframe.style.height = '100%'
      iframe.style.width = '100%'
      // eslint-disable-next-line no-script-url
      iframe.src = 'javascript:false'
      iframe.id = IFRAME_ID
      iframe.crossorigin = 'anonymous'
      iframe.title = IFRAME_TITLE
      iframe.sandbox =
        'allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-scripts allow-same-origin'
      // not supported in safari
      if (iframe.sandbox.supports('allow-presentation')) {
        iframe.sandbox.add('allow-presentation')
      }
      this.iframe = iframe
    }
  }

  mountIframe = options => {
    if (!document.getElementById(IFRAME_ID)) {
      this.iframe.src = this.iframeUrl
      window.addEventListener('message', this.receiveMessage, false)
      const targetEl =
        options.targetElement && document.getElementById(options.targetElement)
      this.wrapper = document.createElement('div')
      this.wrapper.id = this.wrapperId

      this.wrapper.appendChild(this.iframe)

      if (targetEl) {
        targetEl.append(this.wrapper)
      } else {
        document.body.appendChild(this.wrapper)
      }
      this.resizeWrapper()

      window.addEventListener('resize', this.handleWindowResize)
    }
  }

  unpatchViewport = () => {
    if (
      this.viewportEl &&
      this.viewportElCreatedByShim &&
      this.originalViewportContent
    ) {
      this.viewportEl.remove()
    } else if (
      this.viewportEl &&
      !this.viewportElCreatedByShim &&
      this.originalViewportContent
    ) {
      this.viewportEl.setAttribute('content', this.originalViewportContent)
    }
  }

  patchViewport = () => {
    ;[this.viewportEl] = document.querySelectorAll('meta[name="viewport"]')
    if (this.isOpen) {
      if (!this.viewportEl) {
        this.viewportElCreatedByShim = true
        this.viewportEl = document.createElement('meta')
        this.viewportEl.setAttribute('name', 'viewport')
        this.viewportEl.setAttribute('content', VIEWPORT_CONTENT)
        this.originalViewportContent = VIEWPORT_CONTENT
        document.head.appendChild(this.viewportEl)
      } else if (this.originalViewportContent === undefined) {
        this.originalViewportContent = this.viewportEl.getAttribute('content')
        this.viewportEl.setAttribute('content', VIEWPORT_CONTENT)
      }
    } else {
      this.unpatchViewport()
    }
  }

  resizeWrapper = () => {
    const {
      wrapperLayout: { offsetHorizontal },
    } = this
    const width = 415 + offsetHorizontal
    if (window.matchMedia(`(max-width: ${width}px)`).matches) {
      this.resizeFull()
      this.patchViewport()
    } else {
      this.resizeConstrained()
    }
  }

  resizeConstrained = () => {
    if (this.layoutStrategy !== 'constrained') {
      this.layoutStrategy = 'constrained'
      this.dispatchToWidget({
        type: GLOBAL_EVENTS_LAYOUT_CHANGE,
        payload: { strategy: 'constrained', wrapperLayout: {} },
      })
    }
    const el = this.wrapper
    const targetEl =
      this.options.targetElement &&
      document.getElementById(this.options.targetElement)
    const position = this.options.position || this.wrapperLayout.position
    const zIndex =
      this.options.zIndex ||
      this.wrapperLayout.zIndex ||
      Number.MAX_SAFE_INTEGER
    el.style.cssText = `
      z-index: ${zIndex};
      position: ${targetEl ? 'absolute' : 'fixed'};
      bottom: ${this.wrapperLayout.offsetVertical}px;
      left: ${
        position === 'left'
          ? `${this.wrapperLayout.offsetHorizontal}px`
          : 'auto'
      };
      right: ${
        position === 'left'
          ? 'auto'
          : `${this.wrapperLayout.offsetHorizontal}px`
      };
      border: 0;
      width: ${this.wrapperLayout.outerWidth}px;
      height: ${
        this.isOpen
          ? Math.min(
              document.documentElement.clientHeight -
                this.wrapperLayout.offsetVertical,
              this.wrapperLayout.outerHeight
            )
          : this.wrapperLayout.outerHeight
      }px;
    `
  }

  resizeFull = () => {
    if (this.layoutStrategy !== 'full') {
      this.layoutStrategy = 'full'
      this.dispatchToWidget({
        type: GLOBAL_EVENTS_LAYOUT_CHANGE,
        payload: { strategy: 'full' },
      })
    }
    const el = this.wrapper
    const targetEl =
      this.options.targetElement &&
      document.getElementById(this.options.targetElement)
    const position = this.options.position || this.wrapperLayout.position
    const zIndex =
      this.options.zIndex ||
      this.wrapperLayout.zIndex ||
      Number.MAX_SAFE_INTEGER
    const baseStyle = `
      z-index: ${zIndex};
      position: ${targetEl ? 'absolute' : 'fixed'};
      border: 0;
    `
    if (this.isOpen) {
      el.style = `
        ${baseStyle}
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
      `
    } else {
      el.style = `
        ${baseStyle}
        bottom: ${this.wrapperLayout.offsetVertical}px;
      left: ${
        position === 'left'
          ? `${this.wrapperLayout.offsetHorizontal}px`
          : 'auto'
      };
      right: ${
        position === 'left'
          ? 'auto'
          : `${this.wrapperLayout.offsetHorizontal}px`
      };
      width: ${this.wrapperLayout.outerWidth}px;
      height: ${this.wrapperLayout.outerHeight}px;
      `
    }
  }

  runQueuedInit = () => {
    this.embeddable._beforeLoadCallQueue.find(call => {
      const [method, args] = call
      if (method === 'init') {
        this[method](...args)
        return true
      }
      return false
    })
  }

  // "Hydrate" the embeddable object by assigning it the shim's public methods
  finalizeEmbeddable = () => {
    const { embeddable } = this
    PUBLIC_API.forEach(methodName => {
      embeddable[methodName] = this[methodName]
    })
    embeddable.finalized = true
    this.flushQueue()
    if (embeddable.onload) {
      embeddable.onload(this)
    }
    this.processWidgetHashbang()
  }

  flushQueue = () => {
    this.embeddable._beforeLoadCallQueue.forEach(call => {
      const [methodName, args] = call
      if (methodName !== 'init' && PUBLIC_API.includes(methodName)) {
        this[methodName](...args)
      }
    })
    this.embeddable._beforeLoadCallQueue = []
  }

  hashBangRegex = () =>
    /^_grv_(?<widgetUuid>[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})_(?<command>.*?)=(?<value>.*)$/i

  processWidgetHashbang = () => {
    if (window.location.hash === '') return
    const apiLookup = PUBLIC_API.reduce((lookup, apiCommand) => {
      // eslint-disable-next-line no-param-reassign
      lookup[apiCommand.toLowerCase()] = apiCommand
      return lookup
    }, {})
    window.location.hash
      .replace('#', '')
      .split('&')
      .forEach(query => {
        const match = query.match(this.hashBangRegex())
        if (match) {
          let { command, widgetUuid } = match.groups
          const { value } = match.groups
          command = command.toLowerCase()
          widgetUuid = widgetUuid.toLowerCase()
          if (apiLookup[command] && widgetUuid === this.widgetUuid) {
            this[apiLookup[command]](...value.split(','))
          }
        }
      })
  }

  handleWindowResize = debounce(this.resizeWrapper, 300)

  trackPageView = () => {
    if (!this.tracker) {
      // eslint-disable-next-line no-console
      console.warn(TRACKER_DISABLED_WARNING)
      return null
    }
    return this.tracker.pageView()
  }

  trackPageUnload = () => {
    if (!this.tracker) {
      // eslint-disable-next-line no-console
      console.warn(TRACKER_DISABLED_WARNING)
      return null
    }
    return this.tracker.pageUnload()
  }

  trackEvent = (event, properties, occurredAt) => {
    if (!this.tracker) {
      // eslint-disable-next-line no-console
      console.warn(TRACKER_DISABLED_WARNING)
      return null
    }
    return this.tracker.trackEvent(event, properties, occurredAt)
  }

  identifyCompany = (identifyingKey, identifyingValue, properties = null) => {
    if (!this.tracker) {
      // eslint-disable-next-line no-console
      console.warn(TRACKER_DISABLED_WARNING)
      return null
    }
    return this.tracker.identifyCompany(
      identifyingKey,
      identifyingValue,
      properties
    )
  }

  setCompany = properties => {
    if (!this.tracker) {
      // eslint-disable-next-line no-console
      console.warn(TRACKER_DISABLED_WARNING)
      return null
    }
    return this.tracker.setCompany(properties)
  }

  identifyContact = (identifyingKey, identifyingValue, properties = null) => {
    if (!this.tracker) {
      // eslint-disable-next-line no-console
      console.warn(TRACKER_DISABLED_WARNING)
      return null
    }
    return this.tracker.identifyContact(
      identifyingKey,
      identifyingValue,
      properties
    )
  }

  setContact = properties => {
    if (!this.tracker) {
      // eslint-disable-next-line no-console
      console.warn(TRACKER_DISABLED_WARNING)
      return null
    }
    return this.tracker.setContact(properties)
  }

  logout = () => {
    if (!this.tracker) {
      // eslint-disable-next-line no-console
      console.warn(TRACKER_DISABLED_WARNING)
      return null
    }
    return this.tracker.logout()
  }

  setMainScrollElement = element => {
    if (!this.tracker) {
      // eslint-disable-next-line no-console
      console.warn(TRACKER_DISABLED_WARNING)
      return
    }
    this.tracker.setMainScrollElement(element)
  }

  // TODO (jscheel): We will probably want to expose this in a more controlled
  // fashion in our public wodget api.
  pushRoute = (url, as = url, options = {}) => {
    this.dispatchToWidget({
      type: GLOBAL_EVENTS_ROUTE_CHANGE,
      // NOTE (jscheel): Apparently I am re-implementing RPC now.
      payload: { fn: 'push', args: [url, as, options] },
    })
  }

  replaceRoute = (url, as = url, options = {}) => {
    this.dispatchToWidget({
      type: GLOBAL_EVENTS_ROUTE_CHANGE,
      payload: { fn: 'replace', args: [url, as, options] },
    })
  }

  downloadFile = (url, fileName) => {
    return fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob)
        // We have to create an anchor to download the file
        const tempAnchor = document.createElement('a')
        tempAnchor.download = fileName
        tempAnchor.href = blobUrl
        document.body.appendChild(tempAnchor) // for firefox: https://stackoverflow.com/a/32226068
        tempAnchor.click()
        tempAnchor.remove()
      })
  }

  getRoute = () => {
    return this.dispatchToWidget({ type: GLOBAL_EVENTS_GET_ROUTE }, true)
  }

  authorizeAudio = () => {
    if (isIos()) return
    // NOTE (jscheel): We cannot use passive events like mousemove because safari
    // will not accept them as user interactions.
    POSSIBLE_AUDIO_INTERACTION_EVENTS.forEach(eventName => {
      document.addEventListener(eventName, this.playSilence)
    })
  }

  playSilence = () => {
    const audioPath = `${this.environmentUrl}/notifications/silence.mp3`
    this.playAudio(audioPath)
    this.removeAudioInteractionListeners()
  }

  removeAudioInteractionListeners = () => {
    POSSIBLE_AUDIO_INTERACTION_EVENTS.forEach(eventName => {
      document.removeEventListener(eventName, this.playSilence)
    })
  }

  playAudio = url => {
    if (isIos()) return
    if (!url) return
    const audio = new Audio(url)
    try {
      audio.play().catch(() => {
        // do nothing
      })
    } catch {
      // do nothing
    }
  }

  // SHOPIFY
  // https://shopify.dev/themes/trust-security/cookie-banner
  // https://shopify.dev/api/consent-tracking
  isInShopifyOnlineStore = () => {
    return !!window?.Shopify
  }

  shopifyCheckTrackingConsentBeforeLoad = () => {
    this.shopifyLoadConsentTrackingApi(() => {
      if (this.shopifyUserCanBeTracked()) {
        this.loadWidget()
      } else {
        // we can't track so we do not load widget
        // register event listener in case they do accept tracking later
        this.shopifyRegisterTrackingConsentAcceptedListener()
      }
    })
  }

  removeShopifyEventListeners = () => {
    this.shopifyRemoveTrackingConsentAcceptedListener()
  }

  // https://shopify.dev/api/consent-tracking#listening-for-consent-collection
  shopifyRegisterTrackingConsentAcceptedListener = () => {
    this.shopifyRemoveTrackingConsentAcceptedListener()
    document.addEventListener(
      'visitorConsentCollected',
      this.shopifyOnTrackingConsentAccepted
    )
  }

  // https://shopify.dev/docs/api/consent-tracking#migrating-checking-data-processing-permission
  shopifyRemoveTrackingConsentAcceptedListener = () => {
    document.removeEventListener(
      'visitorConsentCollected',
      this.shopifyOnTrackingConsentAccepted
    )
  }

  // https://shopify.dev/docs/api/consent-tracking#using-an-event-listener
  shopifyOnTrackingConsentAccepted = (event = {}) => {
    if ([undefined, null, '', true].includes(event?.detail?.analyticsAllowed)) {
      this.shopifyRemoveTrackingConsentAcceptedListener()
      this.loadWidget()
    }
  }

  // https://shopify.dev/docs/api/consent-tracking#calling-the-api-directly
  shopifyUserCanBeTracked = () => {
    return (
      !window?.Shopify?.customerPrivacy ||
      !window?.Shopify?.customerPrivacy?.analyticsProcessingAllowed ||
      this.shopifyCustomerPrivacyAnalyticsProcessingAllowed()
    )
  }

  shopifyCustomerPrivacyAnalyticsProcessingAllowed = () => {
    return (
      !!window?.Shopify?.customerPrivacy?.analyticsProcessingAllowed &&
      !!window?.Shopify?.customerPrivacy?.analyticsProcessingAllowed()
    )
  }

  shopifyLoadConsentTrackingApi = callback => {
    if (
      !this.isInShopifyOnlineStore() ||
      !window?.Shopify?.loadFeatures ||
      (window?.Shopify?.loadFeatures?.length || 0) < 2 // make sure loadFeatures function also accepts at least 2 arguments
    ) {
      if (isFunction(callback)) {
        callback()
      }
      return
    }

    if (this.shopifyCustomerPrivacyAnalyticsProcessingAllowed()) {
      // the customer privacy consent api is already loaded, no need to load it again
      if (isFunction(callback)) {
        callback()
      }
      return
    }

    window.Shopify.loadFeatures(
      [
        {
          name: 'consent-tracking-api',
          version: SHOPIFY_CONSENT_TRACKING_API_VERSION,
        },
      ],
      error => {
        if (error && Bugsnag.isStarted()) {
          Bugsnag.notify(error)
          return
        }

        if (isFunction(callback)) {
          callback()
        }
      }
    )
  }
}
